import { isSlug } from '@/helpers/slug'
import {
    IPage,
    IPageListItem,
    IPageSearchResult,
    PageListResponseSchema,
    PageSchema,
    PageSearchResponseSchema,
} from '@/schemas/pages'
import { api, apiService } from '@/services/core/ApiService'

// Páginas do CMS. Leituras públicas silenciosas como no legado (o erro ia para o console ou virava "não encontrada"):
// nenhum método usa o tratador padrão de erro, e quem chama decide o feedback. Também rodam no servidor (p/[slug] é
// server component), por isso não gravam store (no servidor a store é um singleton do processo, compartilhado entre
// requisições): no navegador o cache é o do React Query, nos hooks.

const OBJECT_ID = /^[a-f\d]{24}$/i

/** Erro sem toast: o chamador (server component ou hook) decide o que mostrar. */
const silent = () => {}

// ===== LISTAR =====
/** GET /pages/list: páginas ativas, sem `content` (navegação, árvore da ajuda, breadcrumb). */
export const getPagesListService = (): Promise<IPageListItem[]> =>
    apiService(api)
        .get('/pages/list', PageListResponseSchema)
        .onError(silent, { useDefaultErrorHandler: false })
        .execute()

// ===== DETALHE =====
/**
 * GET /pages/get-by-slug/{slug}. `null` quando a página não existe ou está inativa (404) e, sem chamar a API, quando o
 * slug não é um slug (robôs varrem /p/db.sql, /p/.env); outros erros propagam.
 */
export const getPageBySlugService = async (slug: string): Promise<IPage | null> => {
    if (!isSlug(slug)) return null
    return apiService(api)
        .get(`/pages/get-by-slug/${encodeURIComponent(slug)}`, PageSchema)
        .onError(silent, { useDefaultErrorHandler: false, swallow: (error) => error.statusCode === 404 })
        .execute()
}

/**
 * Página pelo id. A API não tem GET /pages/{id} (o legado chamava `/pages/{id}` e sempre caía em "não encontrada"):
 * o id é resolvido na lista de páginas ativas e a página é buscada pelo slug.
 */
export async function getPageByIdService(id: string): Promise<IPage | null> {
    const list = await getPagesListService()
    const item = list.find((p) => p.id === id)
    return item ? getPageBySlugService(item.slug) : null
}

/** Rota /p/[slug]: aceita slug ou id (links antigos e resultados de busca usam o id). */
export const getPageBySlugOrIdService = (slugOrId: string): Promise<IPage | null> =>
    OBJECT_ID.test(slugOrId) ? getPageByIdService(slugOrId) : getPageBySlugService(slugOrId)

// ===== BUSCAR =====
/** GET /pages/search?term=: busca no título e no corpo. Termo em branco devolve lista vazia sem chamar a API. */
export async function searchPagesService(term: string): Promise<IPageSearchResult[]> {
    if (!term.trim()) return []
    return apiService(api)
        .get('/pages/search', PageSearchResponseSchema, { params: { term } })
        .onError(silent, { useDefaultErrorHandler: false })
        .execute()
}
